import { Path } from "app/constants/paths";
import {
  getActiveRouteTrackingRules,
  TrackingPage,
} from "app/utils/route-tracking";
import { configureMetaPixel } from "app/utils/meta-pixel";
import { configureXPixel } from "app/utils/x-pixel";
import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

const firedRoutePixels = new Set<string>();

const getTrackingPage = (
  pathname: string,
  search: string
): TrackingPage | null => {
  const idoMatch = matchPath<{ idoId: string }>(pathname, {
    path: Path.IDO,
    exact: true,
  });

  if (idoMatch?.params.idoId) {
    return {
      type: "ido",
      id: idoMatch.params.idoId,
    };
  }

  const questMatch =
    matchPath(pathname, { path: Path.BUILD_KARMA, exact: true }) ||
    matchPath(pathname, { path: Path.BUILD_KARMA_SLASH, exact: true });

  if (!questMatch) {
    return null;
  }

  const questId = new URLSearchParams(search).get("questId");

  if (!questId) {
    return {
      type: "build_karma",
      id: "build_karma",
    };
  }

  return {
    type: "quest",
    id: questId,
  };
};

const getRouteKey = (
  location: ReturnType<typeof useLocation>,
  page: TrackingPage
): string => {
  const locationKey = location.key || `${location.pathname}${location.search}`;

  return `${locationKey}:${page.type}:${page.id}`;
};

export const useRouteTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const page = getTrackingPage(location.pathname, location.search);
    const rules = getActiveRouteTrackingRules(page, new Date());

    if (!page || rules.length === 0) {
      return;
    }

    const routeKey = getRouteKey(location, page);

    rules.forEach((rule) => {
      const pixelKey = "pixelId" in rule ? rule.pixelId || "" : "";
      const firedKey = `${rule.id}:${rule.provider}:${pixelKey}:${routeKey}`;

      if (firedRoutePixels.has(firedKey)) {
        return;
      }

      firedRoutePixels.add(firedKey);

      if (rule.provider === "x") {
        configureXPixel(rule.pixelId);
      }

      if (rule.provider === "meta" && rule.pixelId) {
        configureMetaPixel(rule.pixelId);
      }
    });
  }, [location]);
};

export default useRouteTracking;
